import React, { useEffect, useId } from 'react';
import { autoUpdate, flip, offset, shift, useFloating } from '@floating-ui/react';
import { Sheet, SheetBody, SheetFooter } from '@mts/ui';
import { X } from 'lucide-react';
import { useLayoutViewport } from '../layout/LayoutViewportContext';
import { useAppOverlay } from './AppOverlayProvider';

const APP_OVERLAY_CONTENT_Z_INDEX = 2147483647;
const APP_OVERLAY_BACKDROP_Z_INDEX = APP_OVERLAY_CONTENT_Z_INDEX - 1;

function AppOverlaySheet({ activeOverlay, closeOverlay, side, size, bodyClassName }) {
  return (
    <Sheet
      open
      onOpenChange={(open) => {
        if (!open) {
          closeOverlay();
        }
      }}
      side={side}
      size={size}
      title={activeOverlay.title}
      description={activeOverlay.description}
      mobileFullScreen={side !== 'bottom'}
      overlayStyle={{ zIndex: APP_OVERLAY_BACKDROP_Z_INDEX, pointerEvents: 'auto' }}
      contentStyle={{ zIndex: APP_OVERLAY_CONTENT_Z_INDEX, pointerEvents: 'auto' }}
      className="mts-app-overlay-content"
    >
      <SheetBody className={bodyClassName}>
        {activeOverlay.content}
      </SheetBody>
      {activeOverlay.footer ? (
        <SheetFooter className="bg-white">
          {activeOverlay.footer}
        </SheetFooter>
      ) : null}
    </Sheet>
  );
}

function FloatingCallout({ activeOverlay, closeOverlay }) {
  const titleId = useId();
  const descriptionId = useId();
  const { refs, floatingStyles, update } = useFloating({
    placement: 'top-end',
    whileElementsMounted: autoUpdate,
    middleware: [offset(12), flip({ padding: 16 }), shift({ padding: 16 })],
  });

  useEffect(() => {
    refs.setReference(activeOverlay.anchorRef?.current || null);
    update?.();
  }, [activeOverlay.anchorRef, refs, update]);

  useEffect(() => {
    const handlePointerDown = (event) => {
      const target = event.target;
      const floatingElement = refs.floating.current;
      const anchorElement = activeOverlay.anchorRef?.current;

      if (
        floatingElement?.contains(target)
        || anchorElement?.contains(target)
      ) {
        return;
      }

      closeOverlay();
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeOverlay();
      }
    };

    document.addEventListener('pointerdown', handlePointerDown, true);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [activeOverlay.anchorRef, closeOverlay, refs.floating]);

  return (
    <div
      ref={refs.setFloating}
      role="dialog"
      aria-modal="false"
      aria-labelledby={activeOverlay.title ? titleId : undefined}
      aria-describedby={activeOverlay.description ? descriptionId : undefined}
      className="mts-app-overlay-content w-[min(26rem,calc(100vw-2rem))] rounded-lg border border-gray-200 bg-white p-4 text-left shadow-xl outline-none"
      style={{
        ...floatingStyles,
        zIndex: APP_OVERLAY_CONTENT_Z_INDEX,
        pointerEvents: 'auto',
      }}
      tabIndex={-1}
    >
      <span className="absolute -bottom-2 right-8 h-4 w-4 rotate-45 border-b border-r border-gray-200 bg-white" aria-hidden="true" />
      {(activeOverlay.title || activeOverlay.description) ? (
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            {activeOverlay.title ? (
              <h2 id={titleId} className="text-base font-semibold text-gray-950">
                {activeOverlay.title}
              </h2>
            ) : null}
            {activeOverlay.description ? (
              <p id={descriptionId} className="mt-1 text-sm leading-6 text-gray-600">
                {activeOverlay.description}
              </p>
            ) : null}
          </div>
          <button
            type="button"
            aria-label="Close overlay"
            title="Close"
            onClick={closeOverlay}
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900"
          >
            <X size={16} aria-hidden="true" />
          </button>
        </div>
      ) : null}
      <div className={activeOverlay.title || activeOverlay.description ? 'mt-3' : ''}>
        {activeOverlay.content}
      </div>
      {activeOverlay.footer ? (
        <div className="mt-4 border-t border-gray-100 pt-3">
          {activeOverlay.footer}
        </div>
      ) : null}
    </div>
  );
}

export function AppOverlayHost() {
  const { activeOverlay, closeOverlay } = useAppOverlay();
  const { isMobile } = useLayoutViewport();

  useEffect(() => {
    if (!activeOverlay || typeof document === 'undefined') {
      return undefined;
    }

    document.body.classList.add('mts-app-overlay-open');
    return () => {
      document.body.classList.remove('mts-app-overlay-open');
    };
  }, [activeOverlay]);

  if (!activeOverlay) {
    return null;
  }

  if (isMobile) {
    return (
      <AppOverlaySheet
        activeOverlay={activeOverlay}
        closeOverlay={closeOverlay}
        side="bottom"
        size="xl"
        bodyClassName="bg-white"
      />
    );
  }

  if (activeOverlay.desktopPresentation === 'floatingCallout') {
    return (
      <FloatingCallout
        activeOverlay={activeOverlay}
        closeOverlay={closeOverlay}
      />
    );
  }

  return (
    <AppOverlaySheet
      activeOverlay={activeOverlay}
      closeOverlay={closeOverlay}
      side="right"
      size="lg"
      bodyClassName="bg-white"
    />
  );
}

export default AppOverlayHost;
